import { translate } from '@/i18n'
import { getStoredSystemDateFormat } from '@/shared/helpers/system-setting-cache'

const apiFieldTokens = [
  'usage_limit_total',
  'usage_limit_per_customer',
  'usage_limit_per_staff',
  'discount_value',
  'min_order_amount',
  'max_discount_amount',
  'purchase_quantity',
  'unit_cost',
  'sale_price',
  'retail_price',
  'cost_price',
  'unit_conversions',
  'supplier_id',
  'purchase_unit_id',
  'material_product_id',
  'output_product_id',
  'output_quantity',
  'recipe_id',
  'production_date',
  'branch_id',
  'procurements',
  'employee_code',
  'full_name',
  'username',
  'password',
  'description',
  'status',
  'phone',
  'email',
  'quantity',
  'guest_count',
  'arrival_time',
  'duration',
  'customer_name',
  'customer_phone',
  'table_ids',
  'deposit_amount',
  'deposit_method',
  'deposit_action',
  'refund_payment_method',
  'name',
  'code',
  'starts_on',
  'ends_on',
] as const

const loyaltyErrorKeys: Record<string, string> = {
  POINTS_BELOW_MINIMUM: 'errors.loyalty.pointsBelowMinimum',
  POINTS_NOT_MULTIPLE_OF_UNIT: 'errors.loyalty.pointsNotMultiple',
  INSUFFICIENT_POINT_BALANCE: 'errors.loyalty.insufficientBalance',
  POINT_REDEMPTION_LIMIT_EXCEEDED: 'errors.loyalty.redemptionLimitExceeded',
  POINT_PARTIAL_PAYMENT_NOT_ALLOWED: 'errors.loyalty.partialPaymentNotAllowed',
  CUSTOMER_NOT_ELIGIBLE: 'errors.loyalty.customerNotEligible',
}

const contractErrorKeys: Record<string, string> = {
  CONTRACT_NOT_TERMINABLE: 'staff.contract.errors.notTerminable',
  CONTRACT_NOT_RENEWABLE: 'staff.contract.errors.notRenewable',
}

const offboardingErrorKeys: Record<string, string> = {
  OFFBOARDING_DUPLICATE_ACTIVE: 'staff.offboarding.errors.OFFBOARDING_DUPLICATE_ACTIVE',
  OFFBOARDING_STAFF_NOT_ACTIVE: 'staff.offboarding.errors.OFFBOARDING_STAFF_NOT_ACTIVE',
  OFFBOARDING_NOT_SUBMITTABLE: 'staff.offboarding.errors.OFFBOARDING_NOT_SUBMITTABLE',
  OFFBOARDING_NOT_PENDING_APPROVAL: 'staff.offboarding.errors.OFFBOARDING_NOT_PENDING_APPROVAL',
  OFFBOARDING_NOT_EDITABLE: 'staff.offboarding.errors.OFFBOARDING_NOT_EDITABLE',
  OFFBOARDING_ALREADY_COMPLETED: 'staff.offboarding.errors.OFFBOARDING_ALREADY_COMPLETED',
  OFFBOARDING_SKIP_REASON_REQUIRED: 'staff.offboarding.errors.OFFBOARDING_SKIP_REASON_REQUIRED',
  OFFBOARDING_DATE_INVALID: 'staff.offboarding.errors.OFFBOARDING_DATE_INVALID',
  OFFBOARDING_DATES_NOT_CONFIRMABLE: 'staff.offboarding.errors.OFFBOARDING_DATES_NOT_CONFIRMABLE',
  OFFBOARDING_NOT_IN_PROGRESS: 'staff.offboarding.errors.OFFBOARDING_NOT_IN_PROGRESS',
  OFFBOARDING_HANDOVER_TASKS_INCOMPLETE:
    'staff.offboarding.errors.OFFBOARDING_HANDOVER_TASKS_INCOMPLETE',
  OFFBOARDING_TASKS_NOT_EDITABLE: 'staff.offboarding.errors.OFFBOARDING_TASKS_NOT_EDITABLE',
  OFFBOARDING_NOT_COMPLETABLE: 'staff.offboarding.errors.OFFBOARDING_NOT_COMPLETABLE',
  OFFBOARDING_COMPLETION_BLOCKED: 'staff.offboarding.errors.OFFBOARDING_COMPLETION_BLOCKED',
  INVALID_STATUS: 'staff.offboarding.completion.INVALID_STATUS',
  DATE_NOT_CONFIRMED: 'staff.offboarding.completion.DATE_NOT_CONFIRMED',
  REQUIRED_TASK_OPEN: 'staff.offboarding.completion.REQUIRED_TASK_OPEN',
  HANDOVER_NOT_CONFIRMED: 'staff.offboarding.completion.HANDOVER_NOT_CONFIRMED',
  FINANCE_NOT_CONFIRMED: 'staff.offboarding.completion.FINANCE_NOT_CONFIRMED',
  ACCESS_NOT_CONFIRMED: 'staff.offboarding.completion.ACCESS_NOT_CONFIRMED',
  ASSET_NOTE_REQUIRED: 'staff.offboarding.completion.ASSET_NOTE_REQUIRED',
  OFFBOARDING_NOT_HOLDABLE: 'staff.offboarding.errors.OFFBOARDING_NOT_HOLDABLE',
  OFFBOARDING_NOT_RESUMABLE: 'staff.offboarding.errors.OFFBOARDING_NOT_RESUMABLE',
  OFFBOARDING_NOT_CANCELLABLE: 'staff.offboarding.errors.OFFBOARDING_NOT_CANCELLABLE',
  OFFBOARDING_SELF_APPROVAL_FORBIDDEN:
    'staff.offboarding.errors.OFFBOARDING_SELF_APPROVAL_FORBIDDEN',
  OFFBOARDING_REASON_REQUIRED: 'staff.offboarding.errors.OFFBOARDING_REASON_REQUIRED',
  OFFBOARDING_REQUIRED_TASK_CANNOT_SKIP:
    'staff.offboarding.errors.OFFBOARDING_REQUIRED_TASK_CANNOT_SKIP',
  OFFBOARDING_ASSET_NOTE_REQUIRED: 'staff.offboarding.errors.OFFBOARDING_ASSET_NOTE_REQUIRED',
  OFFBOARDING_BRANCH_REQUIRED: 'staff.offboarding.errors.OFFBOARDING_BRANCH_REQUIRED',
  OFFBOARDING_DOCUMENT_UNSUPPORTED_TYPE:
    'staff.offboarding.errors.OFFBOARDING_DOCUMENT_UNSUPPORTED_TYPE',
  OFFBOARDING_DOCUMENT_TOO_LARGE: 'staff.offboarding.errors.OFFBOARDING_DOCUMENT_TOO_LARGE',
  OFFBOARDING_DOCUMENT_NOT_FOUND: 'staff.offboarding.errors.OFFBOARDING_DOCUMENT_NOT_FOUND',
  OFFBOARDING_DOCUMENT_INVALID_KEY: 'staff.offboarding.errors.OFFBOARDING_DOCUMENT_INVALID_KEY',
}

const salesErrorKeys: Record<string, string> = {
  WALK_IN_CANNOT_DEBT: 'sales.invoice.errors.walkInCannotDebt',
  'Tạo hóa đơn bán hàng không thành công.': 'sales.invoice.errors.addFailed',
  'Tạo hóa đơn bán hàng không thành công': 'sales.invoice.errors.addFailed',
  'Tạo hoá đơn bán hàng không thành công.': 'sales.invoice.errors.addFailed',
  'Tạo hoá đơn bán hàng không thành công': 'sales.invoice.errors.addFailed',
}

const payrollErrorKeys: Record<string, string> = {
  INSUFFICIENT_LEAVE_QUOTA: 'payroll.errors.insufficient_leave_quota',
  LEAVE_QUOTA_EXHAUSTED: 'payroll.errors.leave_quota_exhausted',
  INSUFFICIENT_LEAVE_QUOTA_TO_REQUEST: 'payroll.errors.insufficient_leave_quota_to_request',
  LEAVE_REQUIRES_SCHEDULE: 'payroll.errors.leave_requires_schedule',
  SYSTEM_LEAVE_TYPE_IMMUTABLE: 'payroll.errors.system_leave_type_immutable',
  LEAVE_TYPE_IN_USE: 'payroll.errors.leave_type_in_use',
  ALLOWANCE_TYPE_IN_USE: 'payroll.errors.allowance_type_in_use',
  REVERSAL_DETAILS_REQUIRED: 'payroll.errors.reversal_details_required',
  RECONCILIATION_REASON_REQUIRED: 'payroll.errors.reconciliation_reason_required',
  LEAVE_QUOTA_BALANCE_NEGATIVE: 'payroll.errors.leave_quota_balance_negative',
  STAFF_MUST_BE_ACTIVE_TO_UPDATE_LEAVE_QUOTA:
    'payroll.errors.staff_must_be_active_to_update_leave_quota',
  INVALID_OVERTIME_RANGE: 'payroll.errors.invalid_overtime_range',
  OVERTIME_OVERLAPS_SHIFT: 'payroll.errors.overtime_overlaps_shift',
  OVERTIME_SHIFT_REQUIRED: 'payroll.errors.overtime_shift_required',
  OVERTIME_NOT_AFTER_SELECTED_SHIFT: 'payroll.errors.overtime_not_after_selected_shift',
  OVERTIME_DELETE_PENDING_ONLY: 'payroll.errors.overtime_delete_pending_only',
  PAYROLL_INPUT_OVERLAP: 'payroll.errors.payroll_input_overlap',
  STAFF_ALLOWANCE_ASSIGNMENT_OVERLAP: 'payroll.errors.staff_allowance_assignment_overlap',
  ATTENDANCE_NOT_ENDED: 'payroll.errors.attendance_not_ended',
  ATTENDANCE_MISSING_CHECK_IN: 'payroll.errors.attendance_missing_check_in',
  ATTENDANCE_UNPAID_LEAVE_NOT_ELIGIBLE: 'staff.attendance.monthly.unpaidLeaveNotEligible',
  ATTENDANCE_MISSING_CHECK_OUT: 'payroll.errors.attendance_missing_check_out',
  ATTENDANCE_UNMATCHED: 'payroll.errors.attendance_unmatched',
  ATTENDANCE_AMBIGUOUS: 'payroll.errors.attendance_ambiguous',
  ADJUSTMENT_PENDING: 'payroll.errors.adjustment_pending',
  LEAVE_PENDING: 'payroll.errors.leave_pending',
  FACEID_PENDING: 'payroll.errors.faceid_pending',
  OVERTIME_PENDING: 'payroll.errors.overtime_pending',
  OVERTIME_NEEDS_REVIEW: 'payroll.errors.overtime_needs_review',
  unconfirmed: 'payroll.errors.attendance_unconfirmed',
  changed_after_confirmation: 'payroll.errors.attendance_changed_after_confirmation',
  ATTENDANCE_CHANGED_AFTER_CONFIRMATION: 'payroll.errors.attendance_changed_after_confirmation',
  adjustment_rejected: 'payroll.errors.adjustment_rejected',
  PAYROLL_PERIOD_NOT_EDITABLE: 'payroll.errors.period_not_editable',
  PAYROLL_PERIOD_LOCKED: 'payroll.errors.period_locked',
  FINALIZED_PERIOD_IMMUTABLE: 'payroll.errors.finalized_period_immutable',
  SALARY_NOT_CONFIGURED: 'payroll.errors.salary_not_configured',
  NO_STANDARD_WORK_DAYS: 'payroll.errors.no_standard_work_days',
  PAYROLL_ATTENDANCE_NOT_LOCKED_OR_CHANGED: 'payroll.errors.attendance_not_locked_or_changed',
  LEAVE_ATTENDANCE_CONFLICT: 'payroll.errors.leave_attendance_conflict',
  MULTIPLE_INSURANCE_PROFILES_IN_MONTH: 'payroll.errors.multiple_insurance_profiles',
  SOCIAL_INSURANCE_PROFILE_CONFLICT: 'payroll.errors.social_insurance_profile_conflict',
  NEGATIVE_NET_SALARY: 'payroll.errors.negative_net_salary',
  PAYROLL_JOB_FAILED_RETRY: 'payroll.errors.job_failed_retry',
  PAYROLL_PERIOD_HAS_PAYROLL_DATA: 'payroll.errors.period_has_payroll_data',
  PAYROLL_PERIOD_HAS_PAYMENTS: 'payroll.errors.period_has_payments',
  PAYROLL_PERIOD_ALREADY_CANCELLED: 'payroll.errors.period_already_cancelled',
  PAYROLL_PERIOD_CANCELLED: 'payroll.errors.period_cancelled',
  CANCEL_REASON_REQUIRED: 'payroll.cancel_table_reason_required',
  CANCEL_REASON_MAX_LENGTH: 'payroll.cancel_table_reason_max_length',
  PAYROLL_TABLE_RANGE_CONFLICT: 'payroll.errors.table_range_conflict',
  PAYROLL_TABLE_ALREADY_EXISTS: 'payroll.errors.table_already_exists',
  PAYROLL_TABLE_NOT_EDITABLE: 'payroll.errors.table_not_editable',
  PAYROLL_TABLE_NOT_DRAFT: 'payroll.errors.table_not_draft',
  PAYROLL_DRAFT_MUST_BE_COMPLETED: 'payroll.errors.draft_must_be_completed',
  STAFF_SELECTION_REQUIRED: 'payroll.errors.staff_selection_required',
  PAYROLL_STAFF_ALREADY_ADDED: 'payroll.errors.staff_already_added',
  PAYROLL_RUN_NOT_EDITABLE: 'payroll.errors.run_not_editable',
  PAYROLL_WORKFLOW_NOT_CONFIGURED: 'payroll.errors.workflow_not_configured',
  PAYSLIP_APPROVAL_REQUIRED: 'payroll.errors.approval_required',
  PAYSLIP_NOT_PAYABLE: 'payroll.errors.payslip_not_payable',
  PAYMENT_EXCEEDS_BALANCE: 'payroll.errors.payment_exceeds_balance',
  PAYSLIP_APPROVAL_IN_PROGRESS: 'payroll.errors.approval_in_progress',
  PAYSLIP_REVISION_STALE: 'payroll.errors.revision_stale',
  PAYSLIP_SEND_NOT_ALLOWED: 'payroll.errors.send_not_allowed',
  PAYSLIP_REVIEW_REQUIRED: 'payroll.errors.review_required',
  PAYSLIP_RESOLUTION_REQUIRED: 'payroll.errors.resolution_required',
  PAYSLIP_ACTION_NOT_ALLOWED: 'payroll.errors.action_not_allowed',
  PAYSLIP_REASON_REQUIRED: 'payroll.errors.reason_required',
  PAYSLIP_NOT_READY_TO_FINALIZE: 'payroll.errors.not_ready_to_finalize',
  PAYROLL_INPUT_CHANGED_RECALCULATE: 'payroll.errors.input_changed_recalculate',
}

const shiftErrorKeys: Record<string, string> = {
  STAFF_SHIFT_OVERLAP: 'staff.shift.errors.shift_overlap',
  SHIFT_DURATION_INVALID: 'staff.shift.errors.shift_duration_invalid',
  STAFF_INACTIVE: 'staff.shift.errors.staff_inactive',
  STAFF_NOT_ASSIGNED_TO_BRANCH: 'staff.shift.errors.staff_not_assigned_to_branch',
  SHIFT_INACTIVE: 'staff.shift.errors.shift_inactive',
  PAST_SHIFT_CHANGE_FORBIDDEN: 'staff.shift.errors.past_shift_change_forbidden',
  SHIFT_BRANCH_IMMUTABLE: 'staff.shift.errors.branch_immutable',
  SHIFT_HAS_FUTURE_ASSIGNMENTS: 'staff.shift.errors.has_future_assignments',
  SHIFT_BREAK_INVALID: 'staff.shift.errors.break_invalid',
  SHIFT_ASSIGNMENT_HAS_ATTENDANCE: 'staff.shift.errors.assignment_has_attendance',
}

const permissionErrorKeys: Record<string, string> = {
  'Permission denied.': 'errors.permissionDenied',
  'Permission denied': 'errors.permissionDenied',
  PERMISSION_DENIED: 'errors.permissionDenied',
  'Role denied.': 'errors.permissionDenied',
  'Role denied': 'errors.permissionDenied',
  ROLE_DENIED: 'errors.permissionDenied',
}

const authErrorKeys: Record<string, string> = {
  'Gửi email không thành công.': 'auth.forgotPassword.feedback.failure',
  'Failed to send email.': 'auth.forgotPassword.feedback.failure',
}

function payrollInputLabel(group: 'Item' | 'Action' | 'Status', token: string) {
  const key = `payroll.input${group}.${token}`
  const label = translate(key)

  return label === key ? token : label
}

export function localizeApiErrorMessage(message: string) {
  const normalizedMessage = message.trim()
  if (/^notice_period_days must be an integer\.?$/i.test(normalizedMessage)) {
    return translate('staff.offboarding.validation.noticePeriodInteger')
  }
  if (/^notice_period_days may not be greater than 365\.?$/i.test(normalizedMessage)) {
    return translate('staff.offboarding.validation.noticePeriodMax')
  }
  const attendanceLine = normalizedMessage.match(
    /^([^/]+)\s*\/\s*(\d{4}-\d{2}(?:-\d{2})?):\s*(.+)$/,
  )
  if (attendanceLine) {
    const [, staffCode, date, reasons] = attendanceLine
    const [year, month, day] = date.split('-')
    const dateFormat = getStoredSystemDateFormat()
    const readableDate = day
      ? dateFormat === 'YYYY-MM-DD' ? date
        : dateFormat === 'MM/DD/YYYY' ? `${month}/${day}/${year}` : `${day}/${month}/${year}`
      : dateFormat === 'YYYY-MM-DD' ? date : `${month}/${year}`
    const readableReasons = reasons
      .split(/,\s*/)
      .map((reason) => {
        const key = payrollErrorKeys[reason]
        return key ? translate(key) : reason
      })
      .join('; ')

    return `${staffCode.trim()} · ${readableDate}: ${readableReasons}`
  }

  const numberedPrefix = normalizedMessage.match(/^(\d+):\s*(.+)$/)
  if (numberedPrefix) {
    const [, , rest] = numberedPrefix
    const localizedRest = rest
      .split(/,\s*/)
      .map((item) => localizeApiErrorMessage(item))
      .join(', ')
    return localizedRest
  }

  const permissionKey = permissionErrorKeys[normalizedMessage]
  if (permissionKey) return translate(permissionKey)

  const authKey = authErrorKeys[normalizedMessage]
  if (authKey) return translate(authKey)

  const contractKey = contractErrorKeys[normalizedMessage]
  if (contractKey) return translate(contractKey)

  const offboardingResumeStatus = normalizedMessage.match(
    /^OFFBOARDING_NOT_RESUMABLE_CURRENT_STATUS:([A-Z_]+)$/,
  )
  if (offboardingResumeStatus) {
    const [, status] = offboardingResumeStatus
    const statusKey = `staff.offboarding.status.${status}`
    const statusLabel = translate(statusKey)

    return translate('staff.offboarding.errors.OFFBOARDING_NOT_RESUMABLE_CURRENT_STATUS', {
      status: statusLabel === statusKey ? status : statusLabel,
    })
  }

  const offboardingKey = offboardingErrorKeys[normalizedMessage]
  if (offboardingKey) return translate(offboardingKey)

  const salesKey = salesErrorKeys[normalizedMessage]
  if (salesKey) return translate(salesKey)

  const payrollKey = payrollErrorKeys[normalizedMessage]
  if (payrollKey) return translate(payrollKey)

  const departmentHasStaff = normalizedMessage.match(/^DEPARTMENT_HAS_STAFF(?::\s*(\d+))?$/)
  if (departmentHasStaff) {
    return translate('staff.department.errors.hasStaff', {
      count: Number(departmentHasStaff[1] ?? 0),
    })
  }

  const invalidTransition = normalizedMessage.match(
    /^INVALID_PAYROLL_TRANSITION:([a-z_]+):([a-z_]+):([a-z_]+)$/,
  )
  if (invalidTransition) {
    return translate('payroll.errors.invalid_transition', {
      action: payrollInputLabel('Action', invalidTransition[2]),
      item: payrollInputLabel('Item', invalidTransition[1]),
      status: payrollInputLabel('Status', invalidTransition[3]),
    })
  }

  const unsupportedTransition = normalizedMessage.match(
    /^PAYROLL_TRANSITION_UNSUPPORTED:([a-z_]+):([a-z_]+)$/,
  )
  if (unsupportedTransition) {
    return translate('payroll.errors.unsupported_transition', {
      action: payrollInputLabel('Action', unsupportedTransition[2]),
      item: payrollInputLabel('Item', unsupportedTransition[1]),
    })
  }

  if (
    /(?:ends?[ _]at|kết thúc tăng ca).*(?:after|sau).*(?:starts?[ _]at|bắt đầu tăng ca)/i.test(
      normalizedMessage,
    )
  ) {
    return translate('payroll.errors.overtime_end_after_start')
  }

  const dateFormatMatch = normalizedMessage.match(
    /^(?:(starts?[ _]on|ends?[ _]on|từ ngày|đến ngày))\s*(?:không phù hợp với định dạng|does not match the format)\s*(.+)$/i,
  )
  if (dateFormatMatch) {
    const rawField = dateFormatMatch[1].toLowerCase().replace(/\s+/g, '_')
    const fieldLabel = ['starts_on', 'từ_ngày'].includes(rawField)
      ? translate('payroll.starts_on')
      : translate('payroll.ends_on')
    return translate('validation.invalid', { field: fieldLabel })
  }

  const loyaltyKey = loyaltyErrorKeys[normalizedMessage]
  if (loyaltyKey) return translate(loyaltyKey)

  const shiftMatch = normalizedMessage.match(/^([A-Z_]+)(?::\s*(.+))?$/)
  if (shiftMatch && shiftErrorKeys[shiftMatch[1]]) {
    const errorKey = shiftErrorKeys[shiftMatch[1]]
    const translated = translate(errorKey)
    if (translated !== errorKey) return translated
  }

  if (
    normalizedMessage === 'CURRENT_PASSWORD_INCORRECT' ||
    /^current\s+(?:password|mật khẩu)\s+is\s+incorrect\.?$/i.test(normalizedMessage)
  ) {
    return translate('auth.changePassword.errors.currentPasswordIncorrect')
  }

  const attendanceMatch = message.match(/^(ATTENDANCE_[A-Z_]+)(?::\s*(.+))?$/)
  if (attendanceMatch) {
    const key = `staff.attendance.errors.${attendanceMatch[1].toLowerCase()}`
    const translated = translate(key, { detail: attendanceMatch[2] ?? '' })

    if (translated !== key) return translated
  }

  return apiFieldTokens.reduce((localizedMessage, field) => {
    const label = translate(`api.fields.${field}`)
    const patternStr = field.includes('_') ? `(?:${field}|${field.replace(/_/g, ' ')})` : field
    // Match field token at start or after whitespace, or prefixed by dot-notation segments (e.g. "procurements.0.purchase_quantity" or "guest count")
    const fieldPattern = new RegExp(
      `(^|\\s)(?:[a-zA-Z0-9_]+\\.)*${patternStr}(?=\\s|$|[^a-zA-Z0-9_])`,
      'gi',
    )

    return localizedMessage.replace(fieldPattern, (_match, prefix: string) => `${prefix}${label}`)
  }, message)
}

function normalizeErrorMessages(messages: string[]) {
  return [
    ...new Set(
      messages
        .map((message) => localizeApiErrorMessage(message.trim()))
        .filter((message) => message !== ''),
    ),
  ]
}

export function getFormErrorMessages(error: unknown, fallback?: string | null) {
  const rawList = Array.isArray(error)
    ? error
    : (error as { validationMessages?: unknown } | null)?.validationMessages

  if (Array.isArray(rawList)) {
    const messages = [
      ...new Set(
        rawList
          .filter((message): message is string => typeof message === 'string')
          .map((message) => message.trim())
          .map((message) => localizeApiErrorMessage(message))
          .filter((message) => message !== ''),
      ),
    ]

    if (messages.length > 0) return messages
  }

  if (error instanceof Error && error.message.trim() !== '') {
    return [localizeApiErrorMessage(error.message.trim())]
  }
  if (typeof error === 'string' && error.trim() !== '') {
    return [localizeApiErrorMessage(error.trim())]
  }
  if (fallback && fallback.trim() !== '') {
    return [localizeApiErrorMessage(fallback.trim())]
  }

  return []
}
